import { MockedProvider } from "@apollo/client/testing/react";
import { MockedAuthManager } from "../../contexts/AuthManager";
import {
    ContractSignatureStatus,
    ContractState,
    EarningType,
    ProfileType,
    TerminatedByType,
} from "../../__generated__/graphql";
import React, { useMemo } from "react";
import { GET_MISSION_WITH_TERMINATION } from "../../graphql/core";
import { addDays } from "date-fns";
import { formatDateForGraphql } from "../../utils";
import MissionDetailsScreen from "../../screens/user/MissionDetails";
import {
    buildCancellationRequest,
    buildCct,
    buildClientInfo,
    buildClientProfile,
    buildClientUser,
    buildContract,
    buildMission,
    buildMissionInfo,
    buildMissionOvertimeStatus,
    buildMissionVacationStatus,
    buildSalaryForecast,
    buildTerminationInfo,
    buildUser,
    buildWorkerInfo,
    buildWorkerProfile,
    getAvatarUrl,
    getAvatarUrl2,
} from "../factories";
import { GET_SALARY_FORECAST } from "../../graphql/salaries";
import type { Meta, StoryObj } from "@storybook/react";
import { allCCTState } from "../../atoms/core";
import { currentLang } from "../../atoms/i18n";
import { GET_CCT_BY_EDITION_BY } from "../../graphql/cct";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import { GET_MISSION_OVERTIME_STATUS, GET_MISSION_VACATION_STATUS } from "../../graphql/timesheet";
import { GET_CLIENT_USERS } from "../../graphql/client";
import { AtomsHydrator } from "../../atoms/atoms-hydrator";
import { MockLink } from "@apollo/client/testing";

function buildMissionData(
    id: string,
    contractState: ContractState,
    earningType: EarningType,
    signatureStatus: ContractSignatureStatus,
    terminatedBy?: TerminatedByType,
    terminationOtherReason?: string,
    startDate?: string,
    endDate?: string,
    effectiveEndDate?: string,
) {
    return buildMission({
        id,
        clientInfo: buildClientInfo({
            id: "the-client-profile-owner",
        }),
        signerInfo: buildClientInfo({
            id: "the-client-profile-owner-2",
        }),
        currentContract: buildContract({
            state: contractState,
            earningType,
            cctContractId: "815002",
            cctEditionId: "12071",
            signatureStatus,
            terminatedBy,
            terminationOtherReason,
            startDate,
            endDate,
            effectiveEndDate,
            workerContractSignedAt:
                contractState === ContractState.Accepted
                    ? formatDateForGraphql(addDays(new Date(), -1))
                    : null,
            clientContractSignedAt:
                contractState == ContractState.Accepted
                    ? formatDateForGraphql(addDays(new Date(), -1))
                    : null,
        }),
    });
}

function buildSalaryForecastMock() {
    const currentDate = new Date();
    currentDate.setDate(1);
    return {
        request: {
            query: GET_SALARY_FORECAST,
            variables: () => true,
        },
        result: () => ({
            data: {
                getSalaryForecast: [buildSalaryForecast()],
            },
        }),
    } as MockLink.MockedResponse;
}
const MOCKS = [
    {
        request: {
            query: GET_CCT_BY_EDITION_BY,
            variables: {
                cctEditionId: "12071",
            },
        },
        result: () => ({
            data: {
                getCctByEditionId: buildCct({
                    name: "Super CCT",
                    cctContractId: "815001",
                    cctEditionId: "12071",
                    cctVersionId: "9",
                }),
            },
        }),
    },
    buildSalaryForecastMock(),
];

function StoryComponent({
    profileType,
    contractState,
    earningType,
    hasError,
    signatureStatus,
    terminatedBy,
    terminationOtherReason,
    startDate,
    endDate,
    effectiveEndDate,
    cancellationRequestedRole,
    availableVacationMinutes,
}: {
    profileType: ProfileType;
    contractState: ContractState;
    earningType: EarningType;
    hasError: boolean;
    signatureStatus?: ContractSignatureStatus;
    terminatedBy?: TerminatedByType;
    terminationOtherReason?: string;
    startDate?: string;
    endDate?: string;
    effectiveEndDate?: string;
    cancellationRequestedRole?: TerminatedByType;
    availableVacationMinutes?: number;
}) {
    const user = useMemo(
        () =>
            buildUser({
                id: "the-owner",
                workerProfile: buildWorkerProfile(),
                clientProfiles: [
                    buildClientProfile({
                        id: "the-client-profile-owner",
                    }),
                ],
                workerInfo: buildWorkerInfo(),
                clientInfos: [
                    buildClientInfo({
                        id: "the-client-profile-owner",
                    }),
                ],
            }),
        [],
    );

    const cctList = useMemo(
        () => [
            buildCct({
                name: "Super CCT",
                cctContractId: "815001",
                cctEditionId: "12071",
                cctVersionId: "9",
            }),
        ],
        [],
    );

    const missionId = `test-mission-id-${earningType}-${contractState}`;
    const realSignatureStatus =
        signatureStatus ||
        (contractState === ContractState.Accepted
            ? ContractSignatureStatus.Success
            : ContractSignatureStatus.NeedSelfSign);
    const mocks = useMemo<MockLink.MockedResponse[]>(() => {
        return [
            ...MOCKS,
            {
                request: {
                    query: GET_MISSION_WITH_TERMINATION,
                    variables: () => true,
                },
                error: hasError ? new Error("Fake mission error") : undefined,
                result: () => ({
                    data: {
                        getMission: buildMissionData(
                            missionId,
                            contractState,
                            earningType,
                            realSignatureStatus,
                            terminatedBy,
                            terminationOtherReason,
                            startDate,
                            endDate,
                            effectiveEndDate,
                        ),
                        getTerminationInfo: buildTerminationInfo(
                            cancellationRequestedRole
                                ? {
                                      cancellationRequest: buildCancellationRequest({
                                          requestedRole: cancellationRequestedRole,
                                      }),
                                  }
                                : undefined,
                        ),
                    },
                }),
            },
            {
                request: {
                    query: GET_MISSION_OVERTIME_STATUS,
                    variables: () => true,
                },
                error: undefined,
                result: () => ({
                    data: {
                        getMissionOvertimeStatus: buildMissionOvertimeStatus({
                            missionInfo: buildMissionInfo({
                                id: missionId,
                                earningType,
                            }),
                            overtimeMinutes: 234,
                        }),
                    },
                }),
            },
            {
                request: {
                    query: GET_MISSION_OVERTIME_STATUS,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        getMissionOvertimeStatus: buildMissionOvertimeStatus({
                            missionInfo: buildMissionInfo({
                                id: missionId,
                                earningType,
                            }),
                            overtimeMinutes: 0,
                        }),
                    },
                }),
            },
            {
                request: {
                    query: GET_MISSION_VACATION_STATUS,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        getMissionVacationStatus: buildMissionVacationStatus({
                            missionInfo: buildMissionInfo({
                                id: missionId,
                                earningType,
                            }),
                            availableVacationMinutes,
                        }),
                    },
                }),
            },
            {
                request: {
                    query: GET_CLIENT_USERS,
                    variables: () => true,
                },
                result: () => ({
                    data: {
                        getClientUsers: [
                            buildClientUser({ id: "the-client-profile-owner" }),
                            buildClientUser({
                                id: "the-client-profile-owner-2",
                                avatarUrl: getAvatarUrl2(),
                                firstName: "Elon",
                                lastName: "Musk",
                            }),
                            buildClientUser({
                                avatarUrl: getAvatarUrl(),
                                firstName: "Mokoto",
                                lastName: "Kusanagi",
                            }),
                        ],
                    },
                }),
            },
        ];
    }, [
        availableVacationMinutes,
        cancellationRequestedRole,
        contractState,
        earningType,
        effectiveEndDate,
        endDate,
        hasError,
        missionId,
        realSignatureStatus,
        startDate,
        terminatedBy,
        terminationOtherReason,
    ]);
    return (
        <AtomsHydrator
            key={earningType}
            atomValues={[
                [currentLang, "fr"],
                [allCCTState, cctList],
            ]}
        >
            <MockedAuthManager user={user}>
                <MockedProfileSwitcherManager
                    profileType={profileType}
                    profileId="the-client-profile-owner"
                >
                    <MockedProvider mocks={mocks} key={earningType}>
                        {/* @ts-expect-error Stack Component need a complex navigation handler */}
                        <MissionDetailsScreen route={{ params: { missionId } }} />
                    </MockedProvider>
                </MockedProfileSwitcherManager>
            </MockedAuthManager>
        </AtomsHydrator>
    );
}

const meta = {
    title: "Screens/MissionDetails",
    component: StoryComponent,
    args: {
        earningType: EarningType.Monthly,
        availableVacationMinutes: 60 * 12.6,
    },
    argTypes: {
        availableVacationMinutes: {
            control: { type: "number" },
        },
        contractState: {
            options: [
                ContractState.Draft,
                ContractState.PendingAcceptation,
                ContractState.Accepted,
            ],
            control: { type: "radio" },
        },
        profileType: {
            options: [ProfileType.Worker, ProfileType.Client],
            control: { type: "radio" },
        },
        earningType: {
            options: [
                EarningType.Monthly,
                EarningType.Daily,
                EarningType.Hourly,
                EarningType.Package,
            ],
            control: { type: "select" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.PendingAcceptation,
        earningType: EarningType.Monthly,
        hasError: false,
    },
};

// The negative balance is the risky case: if the sign is swallowed at render time (BAS-2839), a
// vacation debt reads as a credit. This story makes such a regression visible.
export const WorkerNegativeVacationBalance: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.PendingAcceptation,
        earningType: EarningType.Monthly,
        hasError: false,
        availableVacationMinutes: -60 * 12.6,
    },
};

export const WorkerZeroVacationBalance: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.PendingAcceptation,
        earningType: EarningType.Monthly,
        hasError: false,
        availableVacationMinutes: 0,
    },
};

export const WorkerWithError: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.PendingAcceptation,
        earningType: EarningType.Monthly,
        hasError: true,
    },
};

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
        contractState: ContractState.PendingAcceptation,
        earningType: EarningType.Monthly,
        hasError: false,
    },
};

export const WorkerAccepted: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.Accepted,
        earningType: EarningType.Monthly,
        hasError: false,
    },
};

export const ClientAccepted: Story = {
    args: {
        profileType: ProfileType.Client,
        contractState: ContractState.Accepted,
        earningType: EarningType.Monthly,
        hasError: false,
    },
};

export const ClientTerminatedInFuture: Story = {
    args: {
        profileType: ProfileType.Client,
        contractState: ContractState.Terminated,
        earningType: EarningType.Monthly,
        hasError: false,
        terminatedBy: TerminatedByType.Client,
        terminationOtherReason: "Une bonne raison de résiliation :-)",
        startDate: formatDateForGraphql(addDays(new Date(), -30)),
        endDate: formatDateForGraphql(addDays(new Date(), 10)),
        effectiveEndDate: formatDateForGraphql(addDays(new Date(), 5)),
    },
};

export const ClientTerminatedAndEnded: Story = {
    args: {
        profileType: ProfileType.Client,
        contractState: ContractState.Ended,
        earningType: EarningType.Monthly,
        hasError: false,
        terminatedBy: TerminatedByType.Client,
        terminationOtherReason: "Other reason",
        startDate: formatDateForGraphql(addDays(new Date(), -30)),
        endDate: formatDateForGraphql(addDays(new Date(), -10)),
        effectiveEndDate: formatDateForGraphql(addDays(new Date(), -15)),
    },
};

export const WorkerPackage: Story = {
    args: {
        profileType: ProfileType.Client,
        contractState: ContractState.Accepted,
        earningType: EarningType.Package,
        hasError: false,
    },
};

export const WorkerDaily: Story = {
    args: {
        profileType: ProfileType.Client,
        contractState: ContractState.Accepted,
        earningType: EarningType.Daily,
        hasError: false,
    },
};

// Worker who requested the cancellation themselves: the pending-cancellation banner is shown.
export const WorkerSelfCancellationRequest: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.Accepted,
        earningType: EarningType.Monthly,
        hasError: false,
        cancellationRequestedRole: TerminatedByType.Worker,
    },
};

export const AcceptedWorkerNeedSign: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.Accepted,
        earningType: EarningType.Monthly,
        signatureStatus: ContractSignatureStatus.NeedSelfSign,
        hasError: false,
    },
};
